// @ts-nocheck
/**
 * 🔗 SERVIÇO DE INTEGRAÇÃO COM APLICATIVO NATIVO
 * 
 * Comunicação com JurisHub Signer (aplicativo nativo Windows)
 * Para assinatura digital ITI-compatível
 */

interface NativeSignerConfig {
  baseUrl: string;
  timeout: number;
  retries: number;
}

interface CertificateInfo {
  id: string;
  name: string;
  thumbprint: string;
  issuer: string;
  subject: string;
  validFrom: string;
  validUntil: string;
  hasPrivateKey: boolean;
  friendlyName: string;
  device: string;
  type: 'A1' | 'A3';
}

interface SignatureInfo {
  name?: string;
  reason?: string;
  location?: string;
  contactInfo?: string;
}

interface SignPdfRequest {
  pdfBase64: string;
  certificateThumbprint: string;
  password: string;
  signatureInfo?: SignatureInfo;
}

interface SignPdfResponse {
  success: boolean;
  signedPdf?: string;
  message?: string;
  error?: string;
}

export class NativeSignerService {
  private config: NativeSignerConfig;
  private isAvailable: boolean = false;
  private lastCheck: number = 0;
  private checkInterval: number = 30000; // 30 segundos

  constructor(config: Partial<NativeSignerConfig> = {}) {
    this.config = {
      baseUrl: 'http://localhost:8084',
      timeout: 30000,
      retries: 3,
      ...config
    };
  }

  /**
   * Verificar se o aplicativo nativo está disponível
   */
  async checkAvailability(): Promise<boolean> {
    const now = Date.now();
    
    // Cache por 30 segundos
    if (this.isAvailable && (now - this.lastCheck) < this.checkInterval) {
      return this.isAvailable;
    }

    try {
      console.log('🔍 Verificando disponibilidade do JurisHub Signer...');
      
      const response = await fetch(`${this.config.baseUrl}/api/status`, {
        method: 'GET',
        timeout: 5000
      });

      if (response.ok) {
        const data = await response.json();
        this.isAvailable = data.status === 'online';
        this.lastCheck = now;
        
        if (this.isAvailable) {
          console.log('✅ JurisHub Signer está disponível');
        } else {
          console.log('⚠️ JurisHub Signer não está respondendo');
        }
        
        return this.isAvailable;
      } else {
        this.isAvailable = false;
        this.lastCheck = now;
        return false;
      }
    } catch (error) {
      console.log('❌ JurisHub Signer não está disponível:', error);
      this.isAvailable = false;
      this.lastCheck = now;
      return false;
    }
  }

  /**
   * Listar certificados disponíveis
   */
  async getCertificates(): Promise<CertificateInfo[]> {
    try {
      const available = await this.checkAvailability();
      if (!available) {
        throw new Error('JurisHub Signer não está disponível');
      }

      console.log('📋 Listando certificados do JurisHub Signer...');
      
      const response = await fetch(`${this.config.baseUrl}/api/certificates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        console.log(`✅ ${data.certificates.length} certificados encontrados`);
        return data.certificates;
      } else {
        throw new Error(data.error || 'Erro ao listar certificados');
      }
    } catch (error) {
      console.error('❌ Erro ao listar certificados:', error);
      throw error;
    }
  }

  /**
   * Validar senha do certificado
   */
  async validatePassword(thumbprint: string, password: string): Promise<boolean> {
    try {
      const available = await this.checkAvailability();
      if (!available) {
        throw new Error('JurisHub Signer não está disponível');
      }

      console.log(`🔐 Validando senha do certificado: ${thumbprint}`);
      
      const response = await fetch(`${this.config.baseUrl}/api/certificates/${thumbprint}/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        console.log(data.valid ? '✅ Senha válida' : '❌ Senha inválida');
        return data.valid;
      } else {
        throw new Error(data.error || 'Erro ao validar senha');
      }
    } catch (error) {
      console.error('❌ Erro ao validar senha:', error);
      throw error;
    }
  }

  /**
   * Assinar PDF com certificado nativo
   */
  async signPdf(request: SignPdfRequest): Promise<SignPdfResponse> {
    try {
      const available = await this.checkAvailability();
      if (!available) {
        throw new Error('JurisHub Signer não está disponível. Certifique-se de que o aplicativo está executando.');
      }

      console.log('🔐 Assinando PDF com JurisHub Signer...');
      console.log(`📄 PDF: ${request.pdfBase64.length} chars`);
      console.log(`🔑 Certificado: ${request.certificateThumbprint}`);
      
      const response = await fetch(`${this.config.baseUrl}/api/sign-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        console.log('✅ PDF assinado com sucesso pelo JurisHub Signer!');
        return {
          success: true,
          signedPdf: data.signedPdf,
          message: data.message || 'PDF assinado com assinatura ITI-compatível'
        };
      } else {
        throw new Error(data.error || 'Erro ao assinar PDF');
      }
    } catch (error) {
      console.error('❌ Erro ao assinar PDF:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Upload de certificado A1 para o sistema nativo
   */
  async uploadA1Certificate(request: {
    certificateBase64: string;
    password: string;
    certificateInfo?: any;
  }): Promise<{ success: boolean; certificate?: any; error?: string }> {
    try {
      const available = await this.checkAvailability();
      if (!available) {
        throw new Error('JurisHub Signer não está disponível. Certifique-se de que o aplicativo está executando.');
      }

      console.log('📤 Fazendo upload de certificado A1 para sistema nativo...');
      console.log(`🔑 Certificado: ${request.certificateBase64.length} chars`);
      
      const response = await fetch(`${this.config.baseUrl}/api/upload-a1-certificate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        console.log('✅ Certificado A1 carregado com sucesso no sistema nativo!');
        return {
          success: true,
          certificate: data.certificate
        };
      } else {
        throw new Error(data.error || 'Erro no upload do certificado');
      }
    } catch (error) {
      console.error('❌ Erro ao fazer upload do certificado A1:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Assinar PDF com certificado A1 do sistema web
   */
  async signPdfWithA1(request: {
    pdfBase64: string;
    a1CertificateId: string;
    password: string;
    signatureInfo?: SignatureInfo;
  }): Promise<SignPdfResponse> {
    try {
      const available = await this.checkAvailability();
      if (!available) {
        throw new Error('JurisHub Signer não está disponível. Certifique-se de que o aplicativo está executando.');
      }

      console.log('🔐 Assinando PDF com certificado A1 REAL...');
      console.log(`📄 PDF: ${request.pdfBase64.length} chars`);
      console.log(`🔑 Certificado A1 ID: ${request.a1CertificateId}`);

      // PRIMEIRO: Fazer upload do certificado A1 real
      console.log('📤 Fazendo upload do certificado A1 REAL para o servidor nativo...');
      
      // Buscar dados completos do certificado A1
      const { A1CertificateService } = await import('@/services/a1CertificateService');
      const certificates = await A1CertificateService.getUserCertificates();
      const a1Cert = certificates.find(c => c.id === request.a1CertificateId);
      
      if (!a1Cert) {
        throw new Error('Certificado A1 não encontrado no sistema');
      }
      
      // Descriptografar dados do certificado
      const decryptedBase64 = await A1CertificateService.decryptCertificateData(a1Cert.certificate_data);
      
      // Upload do certificado A1
      const uploadRequest = {
        certificateBase64: decryptedBase64,
        password: request.password,
        certificateInfo: {
          name: a1Cert.certificate_name,
          subject: a1Cert.subject_dn,
          issuer: a1Cert.issuer_dn,
          thumbprint: a1Cert.thumbprint
        }
      };
      
      const uploadResponse = await fetch(`${this.config.baseUrl}/api/upload-a1-certificate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(uploadRequest)
      });
      
      if (!uploadResponse.ok) {
        throw new Error(`Erro no upload do certificado: ${uploadResponse.status}`);
      }
      
      const uploadResult = await uploadResponse.json();
      console.log('✅ Certificado A1 REAL carregado no servidor nativo!');
      console.log('👤 Nome:', uploadResult.certificate.name);
      
      // SEGUNDO: Assinar PDF com certificado A1 carregado
      console.log('🔐 Assinando PDF com certificado A1 REAL carregado...');
      
      const response = await fetch(`${this.config.baseUrl}/api/sign-pdf-a1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        console.log('✅ PDF assinado com sucesso usando certificado A1 REAL!');
        return {
          success: true,
          signedPdf: data.signedPdf,
          message: data.message || 'PDF assinado com certificado A1 real - ITI-compatível'
        };
      } else {
        throw new Error(data.error || 'Erro ao assinar PDF com A1');
      }
    } catch (error) {
      console.error('❌ Erro ao assinar PDF com A1:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Abrir arquivo com aplicativo padrão
   */
  async openFile(filePath: string): Promise<void> {
    try {
      const available = await this.checkAvailability();
      if (!available) {
        throw new Error('JurisHub Signer não está disponível');
      }

      const response = await fetch(`${this.config.baseUrl}/api/open-file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ filePath })
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Erro ao abrir arquivo');
      }
    } catch (error) {
      console.error('❌ Erro ao abrir arquivo:', error);
      throw error;
    }
  }

  /**
   * Obter informações do aplicativo nativo
   */
  async getAppInfo(): Promise<any> {
    try {
      const available = await this.checkAvailability();
      if (!available) {
        throw new Error('JurisHub Signer não está disponível');
      }

      const response = await fetch(`${this.config.baseUrl}/api/status`);
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Erro ao obter informações do app:', error);
      throw error;
    }
  }

  /**
   * Verificar se está disponível (método síncrono)
   */
  isNativeSignerAvailable(): boolean {
    return this.isAvailable;
  }

  /**
   * Forçar verificação de disponibilidade
   */
  async forceAvailabilityCheck(): Promise<boolean> {
    this.lastCheck = 0;
    return await this.checkAvailability();
  }

  /**
   * Configurar timeout personalizado
   */
  setTimeout(timeout: number): void {
    this.config.timeout = timeout;
  }

  /**
   * Configurar URL base personalizada
   */
  setBaseUrl(baseUrl: string): void {
    this.config.baseUrl = baseUrl;
  }
}

// Instância singleton
export const nativeSignerService = new NativeSignerService();

export default NativeSignerService;
